import { useState, useCallback } from "react";
import { assignDoctor, reassignDoctor, unassignDoctor } from "../api/orders";
import type { KitRecord } from "../types/orders";

interface UseAssignmentOptions {
  updateItem: (id: string, updater: (item: KitRecord) => KitRecord) => void;
  showToast: (message: string, variant?: "success" | "error") => void;
}

export function useAssignment({ updateItem, showToast }: UseAssignmentOptions) {
  const [assignTarget, setAssignTarget] = useState<KitRecord | null>(null);
  const [assignMode, setAssignMode] = useState<"assign" | "reassign">("assign");
  const [unassignTarget, setUnassignTarget] = useState<KitRecord | null>(null);

  const handleAssign = useCallback((k: KitRecord) => {
    setAssignMode("assign");
    setAssignTarget(k);
  }, []);

  const handleReassign = useCallback((k: KitRecord) => {
    setAssignMode("reassign");
    setAssignTarget(k);
  }, []);

  const handleUnassign = useCallback((k: KitRecord) => {
    setUnassignTarget(k);
  }, []);

  async function confirmAssign(doctorId: string, doctorName: string) {
    if (!assignTarget) return;
    try {
      const payload = { doctor_user_id: doctorId, kit_code: assignTarget.kit_code };
      if (assignMode === "reassign") {
        await reassignDoctor(assignTarget.record_id, payload);
      } else {
        await assignDoctor(assignTarget.record_id, payload);
      }
      updateItem(assignTarget.record_id, (it) => ({
        ...it,
        assigned_doctor_id: doctorId,
        assigned_doctor_name: doctorName,
      }));
      showToast(`Order ${assignTarget.order_id} assigned to ${doctorName}.`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Assignment failed", "error");
    } finally {
      setAssignTarget(null);
    }
  }

  async function confirmUnassign() {
    if (!unassignTarget) return;
    try {
      await unassignDoctor(unassignTarget.record_id, unassignTarget.kit_code);
      updateItem(unassignTarget.record_id, (it) => ({
        ...it,
        assigned_doctor_id: null,
        assigned_doctor_name: null,
      }));
      showToast("Doctor unassigned");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Unassign failed", "error");
    } finally {
      setUnassignTarget(null);
    }
  }

  return {
    assignTarget,
    assignMode,
    unassignTarget,
    handleAssign,
    handleReassign,
    handleUnassign,
    confirmAssign,
    confirmUnassign,
    closeAssign: () => setAssignTarget(null),
    closeUnassign: () => setUnassignTarget(null),
  };
}
